import type { RosterClaim, RosterDosRange } from './roster.types';

/**
 * Rules for the "apply changes to existing claims" picker (FE-109, step 2 of the save flow).
 *
 * The one rule with consequences: a claim the payer has settled is locked — PAID in the design,
 * ADJUDICATED or COMPLETED as the API reports it. The design's own copy says why —
 * "Claims already paid cannot be amended". A paid claim that is re-sent is a corrected claim
 * against money already remitted, which is a recoupment conversation, not a data fix. So a locked
 * claim can never be selected, and a selection can never hold one — even one picked before the
 * date range changed.
 */

/** Settled by the payer: the design's PAID, and the API's ADJUDICATED / COMPLETED. */
const LOCKED_STATUSES: ReadonlyArray<RosterClaim['status']> = ['PAID', 'ADJUDICATED', 'COMPLETED'];

export function rosterIsClaimLocked(claim: RosterClaim): boolean {
    return LOCKED_STATUSES.includes(claim.status);
}

/** Inclusive on both ends. `dos` and the bounds are `YYYY-MM-DD`, so string comparison is date order. */
export function rosterClaimsInRange(claims: RosterClaim[], range: RosterDosRange): RosterClaim[] {
    return claims.filter(claim => (!range.from || claim.dos >= range.from) && (!range.to || claim.dos <= range.to));
}

export function rosterSelectableClaims(claims: RosterClaim[], range: RosterDosRange): RosterClaim[] {
    return rosterClaimsInRange(claims, range).filter(claim => !rosterIsClaimLocked(claim));
}

/**
 * The selection that would actually be applied: what is picked AND still selectable.
 *
 * Narrowing the date range must drop claims that fell out of it. Otherwise "Update 12 claims"
 * would update claims the user can no longer see.
 */
export function rosterEffectiveSelection(
    selectedIds: string[],
    claims: RosterClaim[],
    range: RosterDosRange,
): string[] {
    const selectable = new Set(rosterSelectableClaims(claims, range).map(claim => claim.id));
    return selectedIds.filter(id => selectable.has(id));
}

/** "Select all in range" when anything selectable is unpicked, "Deselect all" otherwise. */
export function rosterToggleAllInRange(selectedIds: string[], claims: RosterClaim[], range: RosterDosRange): string[] {
    const selectable = rosterSelectableClaims(claims, range).map(claim => claim.id);
    const allSelected = selectable.length > 0 && selectable.every(id => selectedIds.includes(id));
    return allSelected ? [] : selectable;
}

export function rosterToggleClaim(selectedIds: string[], claim: RosterClaim): string[] {
    if (rosterIsClaimLocked(claim)) {
        return selectedIds;
    }
    return selectedIds.includes(claim.id) ? selectedIds.filter(id => id !== claim.id) : [...selectedIds, claim.id];
}

export type RosterDosPreset = 'last30' | 'last90' | 'yearToDate' | 'all';

export const ROSTER_DOS_PRESETS: { key: RosterDosPreset; label: string }[] = [
    { key: 'last30', label: 'Last 30 days' },
    { key: 'last90', label: 'Last 90 days' },
    { key: 'yearToDate', label: 'Year to date' },
    { key: 'all', label: 'All dates' },
];

/**
 * The date range for a preset, relative to `today` (local calendar date).
 *
 * "Year to date" starts on January 1 of `today`'s year. The prototype approximated it as a fixed
 * 218 days, which is only right on one day of the year.
 */
export function rosterDosPresetRange(preset: RosterDosPreset, today: Date): RosterDosRange {
    const to = rosterIsoDate(today);
    switch (preset) {
        case 'last30':
            return { from: rosterIsoDate(addDays(today, -30)), to };
        case 'last90':
            return { from: rosterIsoDate(addDays(today, -90)), to };
        case 'yearToDate':
            return { from: `${today.getFullYear()}-01-01`, to };
        case 'all':
        default:
            return {};
    }
}

/** Local calendar date as `YYYY-MM-DD` — not `toISOString`, which shifts to UTC and can change the day. */
export function rosterIsoDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
    const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    next.setDate(next.getDate() + days);
    return next;
}
